import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ChannelActivityType, ChannelType, EmailActivityChannelDetails } from '@omni/classes/consent/channel.class';
import { MeetingActivityType } from '@omni/classes/activity/activity.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { TrendingCustomer } from '@omni/classes/xperiences/trending.customer.class';
import { MeetingLocationCaptureService } from '@omni/data-services/meeting/meeting-location-capture.service';
import { PageName, NavigationService } from '@omni/services/navigation/navigation.service';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { CaseManagementService } from '@omni/services/case-management/case-management.service';
import { ConsentService } from '@omni/services/consent/consent.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DirectMessagingService } from '@omni/services/direct-messaging/direct-messaging.service';
import { FeatureActionsService } from '@omni/services/feature-actions/feature-actions.service';
import { KitBookingFormService } from '@omni/services/kit-booking/kit-booking-form.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { OpportunityManagementService } from '@omni/services/opportunity-management/opportunity-management.service';
import { UIService } from '@omni/services/ui/ui.service';
import { XperiencesService } from '@omni/services/xperiences/xperiences.service';
import { ActivityService, ActivitySource } from './activity.service';
import { ActivityCreationService, PHONE_NUMBER_FIELDS, QuickCreateContext } from './activity-creation.service';
import { QuickActionMenuService } from './quick-action-menu.service';
import { QuickActionStripService, QuickActionType } from './quick-action-strip.service';

/**
 * Everything a rep can start from the selected contact. It lives here rather than on the
 * contact card because the same strip is offered wherever that contact's glance card is —
 * the details pane and the timeline page — and neither should own the other's list.
 *
 * Callers run change detection themselves after awaiting a create: the menus resolve outside
 * the Angular zone (OMNI-60723).
 */
@Injectable({ providedIn: 'root' })
export class ContactQuickActionsService {

  /** A message is always sent on a channel; with none configured there is nothing to create. */
  private messagingChannels: EmailActivityChannelDetails[] = [];

  constructor(
    private readonly accountService: AccountOfflineService,
    private readonly activityCreationService: ActivityCreationService,
    private readonly activityService: ActivityService,
    private readonly authService: AuthenticationService,
    private readonly caseManagementService: CaseManagementService,
    private readonly consentService: ConsentService,
    private readonly contactService: ContactOfflineService,
    private readonly device: DeviceService,
    private readonly directMessagingService: DirectMessagingService,
    private readonly featureActionService: FeatureActionsService,
    private readonly kitBookingFormService: KitBookingFormService,
    private readonly meetingLocationCaptureService: MeetingLocationCaptureService,
    private readonly navService: NavigationService,
    private readonly notificationService: NotificationService,
    private readonly opportunityService: OpportunityManagementService,
    private readonly quickActionMenu: QuickActionMenuService,
    private readonly strip: QuickActionStripService,
    private readonly translate: TranslateService,
    private readonly uiService: UIService,
    private readonly xperiencesService: XperiencesService,
  ) {
    if (this.authService.hasFeatureAction(FeatureActionsMap.MESSAGE_ACTIVITY)) {
      // Alphabetical, as New Activity lists them, so every Message picker matches (OMNI-63297).
      this.directMessagingService.channelList.subscribe(channels =>
        this.messagingChannels = [...(channels || [])].sort((channelA, channelB) => channelA.title.localeCompare(channelB.title)));
    }
  }

  /** Everything a rep can create from this contact, in the order New Activity lists it. */
  public types(): QuickActionType[] {
    const fa = (action: FeatureActionsMap) => this.authService.hasFeatureAction(action);
    const user = this.authService.user;
    const firstTimeEmailAllowed = () => fa(FeatureActionsMap.MESSAGE_ACTIVITY) && !!user.allowFirstTimeEmailWithoutConsent;
    const phoneCallAllowed = () => fa(FeatureActionsMap.PHONECALL_ACTIVITY);
    return [
      {
        id: 'quick-meeting', labelKey: 'MEETING', icon: 'omni_quick_glance_meeting.svg',
        // No positive gate: the short call launcher is the only thing that hides a meeting.
        isEnabled: () => !(this.featureActionService.isShortCallLauncherEnabledInMobileDevice
          && fa(FeatureActionsMap.SHOR_CALL_LAUNCHER_DISABLE_APPOINTMENT_IN_NEW_ACTIVITY)),
        run: event => this.createMeeting(event),
        menuOptions: () => this.activityCreationService.meetingFormatOptions(),
        runWithOptionId: async id => {
          await this.createMeetingWithFormat(this.activityCreationService.meetingFormats().find(format => format.indskr_activitytypeid === id));
        },
        dictateWithOptionId: async id => {
          const meeting = await this.activityCreationService.dictateMeeting(id, this.context());
          if (meeting) await this.meetingLocationCaptureService.captureContactLocationDistance(meeting);
        },
      },
      {
        id: 'quick-message', labelKey: 'MESSAGE', icon: 'omni_quick_glance_email.svg',
        isEnabled: () => fa(FeatureActionsMap.MESSAGE_ACTIVITY) && this.messagingChannels.length > 0,
        run: event => this.createMessage(false, event),
        menuOptions: () => this.messagingChannels.map(channel => ({
          id: channel.id,
          label: this.directMessagingService.translateNewActivityChannelTitle(channel.title),
        })),
        runWithOptionId: async id => {
          const channel = this.messagingChannels.find(channel => channel.id === id);
          await this.activityCreationService.create('message', { messageChannel: channel, context: this.context() });
        },
      },
      {
        // Offered only while the contact has no active email consent - once they do, the
        // plain Message tile is the way to reach them.
        id: 'request-hcp-consent', labelKey: 'REQUEST_HCP_CONSENT', icon: 'omni_quick_glance_firsttime_email.svg',
        isAllowedForUser: firstTimeEmailAllowed,
        isEnabled: () => firstTimeEmailAllowed()
          && !this.consentService.hasActiveConsentsForEmail(this.contactService.contactInformation, ChannelType.EMAIL, new Date()),
        disabledReason: () => this.device.isOffline ? 'DEVICE_OFFLINE_FEATURE_NOT_SUPPORTED' : null,
        run: event => this.createMessage(true, event),
      },
      {
        id: 'phone-call', labelKey: 'NEW_ACTIVITY_PHONECALL', icon: 'omni_quick_glance_call.svg',
        // Same hide as New Activity; checkIfContactIsAMADoNotContact dereferences contactInformation.
        isAllowedForUser: phoneCallAllowed,
        isEnabled: () => phoneCallAllowed()
          && !(this.authService.hasFeatureAction({ featureAction: FeatureActionsMap.ENABLE_PHONE_CALL_CONSENT_CHECK, skipLDCheck: true })
            && !!this.contactService.contactInformation && this.contactService.checkIfContactIsAMADoNotContact()),
        // New Activity checks every number it could dial; the card used to check mobile alone.
        disabledReason: () => PHONE_NUMBER_FIELDS.some(field => !!this.contactService.contactInformation?.[field])
          ? null : 'XPERIENCES_NO_PHONE_NUMBER',
        run: () => this.createPhoneCall(),
      },
      this.strip.procedureLogType(() => this.context()),
      this.strip.followUpType('contact', () => this.context()),
      {
        id: 'consent', labelKey: 'CONSENT', icon: 'nav/consent.svg',
        isEnabled: () => fa(FeatureActionsMap.CONSENT_TOOL) && !this.uiService.isConsentFromToolDrawer,
        run: () => this.activityCreationService.runExclusive(() => this.captureConsent()),
      },
      {
        id: 'allocation-order', labelKey: 'ALLOWCATION_ORDER', icon: 'nav/customer-allocations.svg',
        isEnabled: () => fa(FeatureActionsMap.ALLOCATION_TOOL) && fa(FeatureActionsMap.ALLOCATION_ORDER_ACTIVITY),
        run: () => this.activityCreationService.create('allocationOrder', { context: this.context() }),
      },
      {
        id: 'sample-delivery-request', labelKey: 'SAMPLE_DELIVERY_REQUEST', icon: 'selected-sample-delivery-request-icon.svg',
        isEnabled: () => fa(FeatureActionsMap.SAMPLE_DELIVERY_REQUESTS) && !this.uiService.isConsentFromToolDrawer,
        run: () => this.activityCreationService.runExclusive(() => this.sampleDeliveryRequest()),
      },
      {
        id: 'customer-inquiry', labelKey: 'CUSTOMER_INQUIRY_WITH_TEXT', icon: 'nav/customer-inquiries.svg',
        isEnabled: () => fa(FeatureActionsMap.CASE_INTAKE) && this.caseManagementService.caseTypeList?.length > 0,
        run: event => this.strip.chooseOptionAndRun(event, this.types(), 'customer-inquiry'),
        menuOptions: () => (this.caseManagementService.caseTypeList ?? []).map(caseType => ({
          id: caseType.id,
          label: caseType.name,
          // Configured per case type and may carry anchors; the menu renders it as a note.
          note: caseType.description,
        })),
        runWithOptionId: async id => {
          const caseType = this.caseManagementService.caseTypeList.find(caseType => caseType.id === id);
          await this.activityCreationService.create('customerInquiry', { caseType, context: this.context() });
        },
      },
      {
        id: 'kit-booking', labelKey: 'KIT_BOOKING', icon: 'kit-booking.svg',
        isEnabled: () => fa(FeatureActionsMap.KIT_BOOKING),
        disabledReason: () => this.activityCreationService.offlineRefusal('kitBooking'),
        run: () => this.activityCreationService.runExclusive(() => this.kitBookingFormService.openCreateForm({
          layoverPushedFrom: 'ContactPageComponent',
          replacesNewActivity: false,
        })),
      },
      {
        // ponytail: no Insights+ glyph exists; trending-up stands in, as it does on the bar.
        id: 'customer-insight', labelKey: 'XPERIENCES_INSIGHTS_PLUS', icon: 'trending-up.svg',
        isEnabled: () => fa(FeatureActionsMap.XPERIENCES),
        // An insight is fetched and scored server side; there is nothing to write offline.
        disabledReason: () => this.activityCreationService.offlineRefusal('customerInsight'),
        run: () => this.activityCreationService.runExclusive(() => this.customerInsight()),
      },
      {
        id: 'opportunity', labelKey: 'OPPORTUNITY', icon: 'nav/opportunities.svg',
        isEnabled: () => fa(FeatureActionsMap.OPPORTUNITY_MANAGEMENT),
        run: () => this.activityCreationService.runExclusive(() => this.newOpportunity()),
      },
      {
        // Last on purpose: with no stored pins the strip is the first `cap` *available* types, so
        // declaration order is the default layout. Sitting beside Customer Inquiry put Medical
        // Inquiry on the strip for any rep missing enough of the earlier actions. It belongs under
        // the overflow until a rep pins it.
        // Shares Customer Inquiry's glyph - no medical-inquiry icon exists yet, and the two feature
        // actions are not granted to the same rep.
        id: 'medical-inquiry', labelKey: 'MEDICAL_INQUIRY', icon: 'nav/customer-inquiries.svg',
        isEnabled: () => fa(FeatureActionsMap.MEDICAL_INQUIRY_TOOL),
        run: () => this.activityCreationService.create('medicalInquiry', { context: this.context() }),
      },
    ];
  }

  private context(): QuickCreateContext {
    return {
      contact: this.contactService.contactInformation
        ? this.contactService.getContactByID(this.contactService.contactInformation['ID'])
        : undefined,
      host: PageName.ContactPageComponent,
    };
  }

  public async createMeeting(event?: Event): Promise<void> {
    // With no choice to make, the create falls back to the format flagged default.
    if (this.activityCreationService.meetingAsksFirst() && event) {
      await this.strip.chooseOptionAndRun(event, this.types(), 'quick-meeting');
      return;
    }
    await this.createMeetingWithFormat();
  }

  // GPS capture stays with the contact card rather than the shared create, as in contact-details.
  private async createMeetingWithFormat(meetingFormat?: MeetingActivityType): Promise<void> {
    const result = await this.activityCreationService.create('meeting', { meetingFormat, context: this.context() });
    if (result.activity) {
      await this.meetingLocationCaptureService.captureContactLocationDistance(result.activity as AppointmentActivity);
    }
  }

  public async createPhoneCall(): Promise<void> {
    await this.activityCreationService.create('phoneCall', {
      context: { ...this.context(), restoreView: 'contactDetails' },
    });
  }

  public async createMessage(firstTimeEmail: boolean = false, event?: Event): Promise<void> {
    if (!this.authService.hasFeatureAction(FeatureActionsMap.MESSAGE_ACTIVITY)) return;
    // OMNI-61541: AMA No-Contact — the contact carries the donotemail flag and cannot be
    // messaged. The service refuses too; this keeps the refusal instant on the tap.
    if (!firstTimeEmail && this.contactService.isContactAMANoContact(this.contactService.contactInformation)) {
      this.notificationService.notify(this.translate.instant('AMA_NO_CONTACT_CANNOT_BE_MESSAGED'), 'Contact Details', 'top', ToastStyle.INFO);
      return;
    }
    this.activityService.activitySource = ActivitySource.CONTACT_INFO;

    const channel = await this.pickMessageChannel(firstTimeEmail, event);
    if (!channel) return;
    await this.activityCreationService.create('message', {
      messageChannel: channel,
      firstTimeEmail,
      context: this.context(),
    });
  }

  /**
   * One channel needs no question; several do, and with no tile to anchor to the first wins.
   *
   * A first-time email request is an email by definition, so it never offers the other
   * channels — with a single email channel configured it asks nothing at all.
   */
  private async pickMessageChannel(firstTimeEmail: boolean, event?: Event): Promise<EmailActivityChannelDetails | undefined> {
    const channels = firstTimeEmail
      ? this.messagingChannels.filter(channel => channel.activityType === ChannelActivityType.EMAIL)
      : this.messagingChannels;
    if (!channels.length) return undefined;
    if (channels.length === 1 || !event) return channels[0];

    // First time email is its own row, so the sub-list it opens is the email channels only.
    const picked = await this.quickActionMenu.pickOption(event, 'quick-message',
      this.translate.instant('MESSAGE'),
      channels.map(channel => ({ id: channel.id, label: this.directMessagingService.translateNewActivityChannelTitle(channel.title) })));
    return channels.find(channel => channel.id === picked?.optionId);
  }

  /**
   * Consent capture writes nothing here - it opens the capture page, and the rep saves from
   * it. New Activity's own consent row does exactly this, so going through it only added a
   * screen; the only difference is the master page, which was PresentationMeetingComponent
   * there because that row was written for the in-meeting flow.
   */
  private captureConsent(): Promise<unknown> {
    return this.navService.pushChildNavPageWithPageTracking(
      PageName.ContactConsentCaptureComponent,
      PageName.ContactConsentCaptureComponent,
      PageName.ContactPageComponent,
      { asPage: true },
    );
  }

  /**
   * Another form rather than a create. New Activity's row opens the same page with the same
   * contact, so going through it only added a screen; the page already defaults its own
   * master page to ContactPageComponent, which is where it is opened from.
   */
  private sampleDeliveryRequest(): Promise<unknown> {
    return this.navService.pushChildNavPageWithPageTracking(
      PageName.NewSampleDeliveryRequestComponent,
      PageName.NewSampleDeliveryRequestComponent,
      PageName.ContactPageComponent,
      { contact: this.contactService.contactInformation, from: PageName.ContactPageComponent },
    );
  }

  /**
   * An insight is not an activity: nothing is created here, the insight page is opened with
   * the customer already on it. New Activity's own row does the same, so the hop it added
   * bought nothing.
   */
  private async customerInsight(): Promise<unknown> {
    if (this.device.isOffline) return;
    const insight = new TrendingCustomer();
    insight.contactid = this.contactService.contactInformation.ID;
    insight.fullname = this.contactService.contactInformation.fullname;
    this.xperiencesService.newInsight = insight;
    this.uiService.showNewActivity = false;
    this.uiService.activeView = 'ContactPageRightPaneNav';
    return this.navService.pushChildNavPageWithPageTracking(
      PageName.CustomerInsightComponent,
      PageName.CustomerInsightComponent,
      PageName.ContactPageComponent,
      { from: PageName.ContactPageComponent },
    );
  }

  /**
   * Opens the form in the contact's own pane, as a plan's Opportunities section does. The contact
   * rides on as a stakeholder, so it needs a known account and a role to hold; New Activity refuses the same.
   */
  private async newOpportunity(): Promise<void> {
    const contact = this.contactService.contactInformation;
    const account = (contact?.accountRelationships ?? [])
      .map(relationship => this.accountService.getAccountById(relationship.accountId)).find(Boolean);
    if (!account || !this.opportunityService.stakeholderRoles.length) {
      this.notificationService.notify(this.translate.instant('NEW_OPPORTUNITY_TOAST_NO_ACCOUNT'), 'Contact Details', 'top', ToastStyle.DANGER);
      return;
    }
    await this.navService.pushChildNavPageWithPageTracking(
      PageName.NewOpportunityComponent,
      PageName.NewOpportunityComponent,
      PageName.ContactPageComponent,
      { from: PageName.ContactPageComponent },
    );
    this.navService.setChildNavRightPaneView(true);
    this.opportunityService.prefillNewOpportunity(account, contact);
  }
}
